import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
} from '@mui/material';
import {
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from "@mui/icons-material/Campaign";
import HistoryIcon from "@mui/icons-material/History";
import WarningIcon from "@mui/icons-material/Warning";
import EventIcon from '@mui/icons-material/Event';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getApprovedNotices, withdrawNotice } from '../db/noticeApi';

const NoticeBoard = () => {
    const { user } = useAuth();
    const [notices, setNotices] = useState([]);
    const [category, setCategory] = useState("active");
    const activeCount = notices.filter(n => n.status === "approved").length;
    const withdrawnCount = notices.filter(n => n.status === "withdrawn").length;
    const expiredCount = notices.filter(n => n.status === "marked_for_deletion").length;
    const [viewAttachment, setViewAttachment] = useState(false);
    const [attachmentPreview, setAttachmentPreview] = useState([]);
    const [activeAttachment, setActiveAttachment] = useState(null);
    const [tenantType, setTenantType] = useState(null);
    const [facilityType, setFacilityType] = useState(null);
    const filteredNotices = notices.filter((n) => {
        if (category === "withdrawn") return n.status === "withdrawn";
        if (category === "expired") return n.status === "marked_for_deletion";
        return n.status === "approved";
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };
    const [selectedNotice, setSelectedNotice] = useState(null);

    const loadNotices = async () => {
        if (!user?.tenantId) return;

        const data = await getApprovedNotices(user.tenantId, true);
        const now = new Date();
        const updated = data.map((notice) => {
            if (
                notice.status === 'approved' &&
                notice.targetDate &&
                notice.targetDate.toDate() < now
            ) {
                return { ...notice, status: 'marked_for_deletion' };
            }
            return notice;
        });
        setNotices(updated);
    };

    useEffect(() => {
        const loadTenantAndFacility = async () => {

            if (!user?.tenantId) return;

            // Load tenant type
            const tenantRef = doc(db, "tenantmaster", user.tenantId);
            const tenantSnap = await getDoc(tenantRef);

            if (tenantSnap.exists()) {
                setTenantType(tenantSnap.data().tenantType);
            }

            // Load facility type for mixed tenants
            if (user?.facilityId) {

                const facilityRef = doc(
                    db,
                    "tenantmaster",
                    user.tenantId,
                    "facilities",
                    user.facilityId
                );

                const facilitySnap = await getDoc(facilityRef);

                if (facilitySnap.exists()) {
                    setFacilityType(facilitySnap.data().facilityType);
                }
            }
        };
        loadTenantAndFacility();
        loadNotices();
    }, [user]);

    const handleWithdraw = async (noticeId) => {
        await withdrawNotice(noticeId);
        showSnackbar('Notice Withdrawn', 'success');
        loadNotices();
    };

    const canWithdraw =
        (user?.role === 'appadmin' || user?.role === 'facilityadmin') &&
        tenantType !== 100;

    // Access control
    const facilityTypeValue =
        facilityType !== null
            ? facilityType
            : tenantType === 300
                ? null
                : tenantType;

    if (facilityTypeValue !== null) {

        const residentialAllowedRoles = [
            "host",
            "appadmin",
            "appoperator",
            "facilityadmin"
        ];

        if (facilityTypeValue === 100 && !residentialAllowedRoles.includes(user?.role)) {
            return (
                <Container>
                    <Typography align="center" sx={{ mt: 10 }}>
                        Access Denied
                    </Typography>
                </Container>
            );
        }

        if (facilityTypeValue === 200 && user?.role === "host") {
            return (
                <Container>
                    <Typography align="center" sx={{ mt: 10 }}>
                        Access Denied
                    </Typography>
                </Container>
            );
        }
    }

    return (
        <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
            <Container maxWidth="xl" sx={{ mt: 4 }}>

                {/* BOARD FRAME */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #d2a679 0%, #b97a56 100%)',
                        minHeight: '85vh',
                        p: 5,
                        borderRadius: 4,
                        border: '12px solid #6d4c41',
                        boxShadow: 'inset 0 0 50px rgba(0,0,0,0.25)'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: '#3e2723'
                            }}
                        >
                            📌 {category === "withdrawn"
                                ? "Withdrawn Notices"
                                : category === "expired"
                                    ? "Expired Notices"
                                    : "Notice Board"}
                        </Typography>

                        <Box>
                            <ToggleButtonGroup
                                value={category}
                                exclusive
                                onChange={(e, newValue) => {
                                    if (newValue) setCategory(newValue);
                                }}
                                sx={{
                                    background: "#fff",
                                    borderRadius: 3,
                                    boxShadow: 2
                                }}
                            >

                                <ToggleButton value="active">
                                    <CampaignIcon sx={{ mr: 1 }} />
                                    Active ({activeCount})
                                </ToggleButton>

                                <ToggleButton value="withdrawn">
                                    <HistoryIcon sx={{ mr: 1 }} />
                                    Withdrawn ({withdrawnCount})
                                </ToggleButton>

                                <ToggleButton value="expired">
                                    <WarningIcon sx={{ mr: 1 }} />
                                    Expired ({expiredCount})
                                </ToggleButton>

                            </ToggleButtonGroup>
                        </Box>

                    </Box>

                    <Grid container spacing={4}>
                        {filteredNotices.map((notice) => {

                            const rotation = Math.floor(Math.random() * 6) - 3;
                            const paperColor = Math.random() > 0.5 ? '#fffdf7' : '#fff9c4';

                            return (
                                <Grid item xs={12} sm={6} md={4} key={notice.id}>

                                    <Paper
                                        elevation={6}
                                        onClick={() => setSelectedNotice(notice)}
                                        sx={{
                                            p: 3,
                                            position: 'relative',
                                            background: paperColor,
                                            borderRadius: 1,
                                            transform: `rotate(${rotation}deg)`,
                                            boxShadow: '0 12px 25px rgba(0,0,0,0.35)',
                                            transition: '0.3s',
                                            border: '1px solid #e0d8c3',
                                            '&:hover': {
                                                transform: 'rotate(0deg) scale(1.04)'
                                            }
                                        }}
                                    >

                                        {/* Pin */}
                                        <Box
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                backgroundColor: '#c62828',
                                                borderRadius: '50%',
                                                position: 'absolute',
                                                top: -10,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.4)'
                                            }}
                                        />

                                        {/* Date Stamp */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 10,
                                                right: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                background: '#ef5350',
                                                color: '#fff',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.75rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            <EventIcon sx={{ fontSize: 14 }} />
                                            {notice.targetDate?.toDate().toLocaleDateString()}
                                        </Box>

                                        {/* Title */}
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mt: 4,
                                                fontWeight: 700,
                                                letterSpacing: 0.5,
                                                textDecoration:
                                                    notice.status === 'withdrawn' ||
                                                        notice.status === 'marked_for_deletion' ||
                                                        notice.status === 'rejected'
                                                        ? 'line-through'
                                                        : 'none'
                                            }}
                                        >
                                            {notice.title}
                                        </Typography>

                                        {/* Preview */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 1,
                                                color: '#444',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {notice.content.length > 120
                                                ? notice.content.slice(0, 120) + '...'
                                                : notice.content}
                                        </Typography>

                                        {/* Buttons */}
                                        <Box sx={{ mt: 2 }}>
                                            {canWithdraw && notice.status === 'approved' && user?.role !== "host" && (
                                                <IconButton
                                                    sx={{
                                                        ml: 1,
                                                        background: '#b71c1c',
                                                        color: '#fff',
                                                        '&:hover': { background: '#7f0000' }
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleWithdraw(notice.id);
                                                    }}
                                                >
                                                    <DeleteForeverIcon />
                                                </IconButton>
                                            )}
                                        </Box>

                                    </Paper>

                                </Grid>
                            );
                        })}
                    </Grid>

                    {notices.length === 0 && (
                        <Typography align="center" sx={{ mt: 6, color: '#4e342e' }}>
                            No notices available
                        </Typography>
                    )}
                </Box>

                {/* MODAL */}
                <Dialog
                    open={Boolean(selectedNotice)}
                    onClose={() => setSelectedNotice(null)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogContent sx={{ p: 0, background: '#f5f5f5' }}>

                        <Box
                            sx={{
                                background: '#ffffff',
                                p: 5,
                                minHeight: 500,
                                position: 'relative',
                                fontFamily: 'Georgia, serif'
                            }}
                        >

                            {/* WATERMARK IF WITHDRAWN / REJECTED / EXPIRED */}
                            {(selectedNotice?.status === 'withdrawn' ||
                                selectedNotice?.status === 'rejected' ||
                                selectedNotice?.status === 'marked_for_deletion') && (
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            top: '40%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-30deg)',
                                            fontSize: '4rem',
                                            color: 'rgba(255,0,0,0.1)',
                                            fontWeight: 800,
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {selectedNotice.status === 'marked_for_deletion'
                                            ? 'EXPIRED'
                                            : selectedNotice.status.toUpperCase()}
                                    </Typography>
                                )}

                            {/* HEADER */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {selectedNotice?.type?.toUpperCase() || "NOTICE"}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                        Published on {selectedNotice?.createdAt?.toDate().toLocaleDateString()}
                                    </Typography>

                                    <Typography variant="body2">
                                        Published by {
                                            selectedNotice?.createdByRole === "appadmin"
                                                ? "Tenant Admin"
                                                : selectedNotice?.createdByRole === "facilityadmin"
                                                    ? "Facility Admin"
                                                    : selectedNotice?.createdByRole || "Admin"
                                        }
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ borderBottom: '2px solid #000', mb: 3 }} />

                            {/* TITLE */}
                            <Typography
                                variant="h6"
                                sx={{
                                    textAlign: 'left',
                                    fontWeight: 700,
                                    mb: 3,
                                    textDecoration:
                                        selectedNotice?.status === 'withdrawn' ||
                                            selectedNotice?.status === 'marked_for_deletion' ||
                                            selectedNotice?.status === 'rejected'
                                            ? 'line-through'
                                            : 'none'
                                }}
                            >
                                {selectedNotice?.title}
                            </Typography>

                            {/* CONTENT */}
                            <Typography
                                variant="body1"
                                sx={{
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.8,
                                    mb: 4
                                }}
                            >
                                {selectedNotice?.content}
                            </Typography>

                            {/* TARGET DATE */}
                            <Typography variant="body2" sx={{ mb: 3 }}>
                                Target Date: {selectedNotice?.targetDate?.toDate().toLocaleDateString()}
                            </Typography>

                            {/* SIGNATURE */}
                            {selectedNotice?.signatureUrl && (
                                <Box sx={{ textAlign: 'right', mt: 5 }}>
                                    <Typography variant="subtitle2">
                                        Authorized Signatory
                                    </Typography>
                                    <img
                                        src={selectedNotice.signatureUrl}
                                        alt="Signature"
                                        style={{ maxWidth: '200px', marginTop: '10px' }}
                                    />
                                </Box>
                            )}

                            {/* ATTACHMENTS */}
                            {selectedNotice?.attachments?.length > 0 && (
                                <Box sx={{ mt: 5 }}>

                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Attachments:
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        sx={{ mt: 1 }}
                                        onClick={() => {
                                            setAttachmentPreview(selectedNotice.attachments);
                                            setActiveAttachment(selectedNotice.attachments[0]);
                                            setViewAttachment(true);

                                        }}
                                    >
                                        View Attachments ({selectedNotice.attachments.length})
                                    </Button>

                                    {/* Attachment URLs */}
                                    <Box sx={{ mt: 2 }}>
                                        {selectedNotice.attachments.map((file, index) => (
                                            <Typography
                                                key={index}
                                                variant="body2"
                                                sx={{ mt: 0.5 }}
                                            >
                                                <a
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {file.name}
                                                </a>
                                            </Typography>
                                        ))}
                                    </Box>

                                </Box>
                            )}

                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: 'flex-end' }}>
                        <Button onClick={() => setSelectedNotice(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={viewAttachment}
                    onClose={() => setViewAttachment(false)}
                    maxWidth="lg"
                    fullWidth
                >

                    <DialogTitle>
                        Attachments ({attachmentPreview.length})
                    </DialogTitle>

                    <DialogContent>

                        <Grid container spacing={2}>

                            {/* LEFT PANEL — FILE LIST */}

                            <Grid item xs={4}>

                                <Box
                                    sx={{
                                        borderRight: "1px solid #ddd",
                                        height: 500,
                                        overflowY: "auto"
                                    }}
                                >

                                    {attachmentPreview.map((file, index) => (

                                        <Box
                                            key={index}
                                            onClick={() => setActiveAttachment(file)}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                p: 2,
                                                cursor: "pointer",
                                                borderBottom: "1px solid #eee",
                                                background:
                                                    activeAttachment?.url === file.url
                                                        ? "#e3f2fd"
                                                        : "transparent",
                                                "&:hover": { background: "#f5f5f5" }
                                            }}
                                        >

                                            <Typography variant="body2">

                                                {file.type === "image"
                                                    ? "🖼 Image"
                                                    : "📄 PDF"} — {file.name}

                                            </Typography>

                                        </Box>

                                    ))}

                                </Box>

                            </Grid>

                            {/* RIGHT PANEL — PREVIEW */}

                            <Grid item xs={8}>

                                <Box
                                    sx={{
                                        height: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >

                                    {activeAttachment ? (

                                        activeAttachment.type === "image" ? (

                                            <img
                                                src={activeAttachment.url}
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    borderRadius: 6
                                                }}
                                            />

                                        ) : (

                                            <iframe
                                                src={activeAttachment.url}
                                                width="100%"
                                                height="100%"
                                                style={{ border: "none" }}
                                            />

                                        )

                                    ) : (

                                        <Typography>Select a file to preview</Typography>

                                    )}

                                </Box>

                            </Grid>

                        </Grid>

                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setViewAttachment(false)}>
                            Close
                        </Button>
                    </DialogActions>

                </Dialog>

                {/* SNACKBAR */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default NoticeBoard;